import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Enquiry } from "../data/enquiries/types";
import { enquiryApi } from "../data/enquiries/api";
import { toast } from "@shadcn/hooks/use-toast";
import { useEnquiry } from '@/data';
import { Schema } from "@schema-types/types";
import { setEnquiryId, trackJourneyCompleted, trackJourneyStarted } from "@/data/analytics/ga4";
import { getBrowserIanaTimeZoneId } from "@schema-types/enquiryEvaluationTimeZone";
import { apiClient } from "@/data/axiosConfig";

declare global {
  interface Window {
    __JOURNEY__?: Schema.JourneySchema;
    __JOURNEY_VERSION__?: string;
    __TURNSTILE__?: { siteKey: string | null };
  }
}

interface UserJourneyContextType {
  journey: Schema.JourneySchema | null;
  journeyVersion: string | null;
  journeyDisplayId: string;
  journeyLoading: boolean;
  journeyError: string | null;
  journeyNotFound: boolean;
  enquiry: Enquiry | null;
  enquiryLoading: boolean;
  enquiryError: string | null;
  isLoading: boolean;
  isSubmitting: boolean;
  updateStepData: (stepId: string, data: Record<string, string | string[]>) => Promise<string | null>;
  moveToNextStep: (nextStepId: string) => Promise<void>;
  createEnquiry: () => Promise<Enquiry>;
  updateEnquiry: (updatedEnquiry: Enquiry) => void;
  showNotification: (message: string, severity: "success" | "error" | "info") => void;
  turnstileTokenRef: React.MutableRefObject<string | null>;
  turnstileFeatureEnabled: boolean;
}

const UserJourneyContext = createContext<UserJourneyContextType | null>(null);

interface UserJourneyProviderProps {
  children: ReactNode;
  journeyDisplayId: string;
}

export const UserJourneyProvider = ({ children, journeyDisplayId }: UserJourneyProviderProps) => {
  const journey = window.__JOURNEY__ ?? null;
  const journeyVersion = window.__JOURNEY_VERSION__ ?? null;
  const journeyLoading = false;
  const journeyErrorObj: Error | null = null;
  const journeyNotFound = false;

  const {
    enquiry: loadedEnquiry,
    isLoading: enquiryLoading,
    error: enquiryError,
    createEnquiry: createEnquiryHook,
  } = useEnquiry(journey?.id ?? null, journeyVersion ?? null);

  const [enquiry, setEnquiry] = useState<Enquiry | null>(loadedEnquiry);

  useEffect(() => {
    setEnquiry(loadedEnquiry);
  }, [loadedEnquiry]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);
  const turnstileTokenRef = useRef<string | null>(null);
  const [turnstileFeatureEnabled, setTurnstileFeatureEnabled] = useState(false);

  useEffect(() => {
    apiClient
      .get<{ turnstile: boolean }>("/api/feature-flags")
      .then((response) => {
        setTurnstileFeatureEnabled(response.data.turnstile === true);
      })
      .catch(() => {
        setTurnstileFeatureEnabled(false);
      });
  }, []);

  const showNotification = (message: string, severity: "success" | "error" | "info") => {
    toast({
      description: message,
      variant: severity === "error" ? "destructive" : "default",
    });
  };

  const createEnquiry = async (skipStateUpdate = false): Promise<Enquiry> => {
    const token = turnstileTokenRef.current;
    turnstileTokenRef.current = null;
    const newEnquiry = await createEnquiryHook(token);
    if (!skipStateUpdate) {
      setEnquiry(newEnquiry);
    }
    return newEnquiry;
  };

  const updateEnquiry = (updatedEnquiry: Enquiry) => {
    setEnquiry(updatedEnquiry);
  };

  const moveToNextStep = async (nextStepId: string) => {
    setEnquiry((current) =>
      current
        ? {
            ...current,
            currentStepId: nextStepId,
          }
        : current
    );
  };

  const updateStepData = async (
    stepId: string,
    data: Record<string, string | string[]>
  ): Promise<string | null> => {
    if (isSubmittingRef.current) return null;
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      let currentEnquiry = enquiry;
      const wasEnquiryCreated = !currentEnquiry;
      if (!currentEnquiry) {
        // Don't update enquiry state during submission to prevent flash
        // We'll update it with the full data after the API call completes
        currentEnquiry = await createEnquiry(true);
        if (currentEnquiry) {
          setEnquiryId(currentEnquiry.id);
          trackJourneyStarted(currentEnquiry.id);
        }
      }

      if (!currentEnquiry) {
        throw new Error("Unable to create enquiry");
      }

      // Prepare the updated enquiry for the API call
      const updatedStepData = [...currentEnquiry.stepData];
      const stepDataIndex = updatedStepData.findIndex((sd) => sd.stepId === stepId);
      const mergedData = stepDataIndex >= 0
        ? { ...updatedStepData[stepDataIndex].data, ...data }
        : { ...data };

      if (stepDataIndex >= 0) {
        updatedStepData[stepDataIndex] = {
          ...updatedStepData[stepDataIndex],
          data: mergedData,
        };
      } else {
        updatedStepData.push({ stepId, data: mergedData });
      }

      const updatedEnquiry = {
        ...currentEnquiry,
        stepData: updatedStepData,
      };

      // Skip optimistic update if we just created the enquiry to prevent flash
      // We'll update it with the refreshed data from the server instead
      if (!wasEnquiryCreated) {
        setEnquiry(updatedEnquiry);
      }

      if (!journeyVersion) {
        throw new Error("Journey version is not available");
      }

      const pipelineResponse = await enquiryApi.executeNextStep({
        journeyId: updatedEnquiry.journeyId,
        enquiryId: updatedEnquiry.id,
        version: journeyVersion,
        currentStepId: stepId,
        currentStepData: data,
        clientTimeZoneId: getBrowserIanaTimeZoneId(),
      });

      if (!pipelineResponse.success) {
        throw new Error(pipelineResponse.errorMessage || "Failed to execute next step");
      }

      // Refresh the enquiry from the server to get the latest data
      // This ensures we have the correct step data and currentStepId after the backend has saved it
      const refreshedEnquiry = await enquiryApi.get(updatedEnquiry.id);
      
      if (pipelineResponse.isComplete) {
        setEnquiry(refreshedEnquiry);
        trackJourneyCompleted();
        // Stay on the current page when journey is complete
        return null;
      }

      if (pipelineResponse.nextStepId) {
        // Update the refreshed enquiry with the next step ID
        const enquiryWithNextStep = {
          ...refreshedEnquiry,
          currentStepId: pipelineResponse.nextStepId,
        };
        setEnquiry(enquiryWithNextStep);
        return pipelineResponse.nextStepId;
      }

      setEnquiry(refreshedEnquiry);
      return null;
    } catch (error) {
      if (!(error as any)?.isRateLimitError) {
        showNotification(
          error instanceof Error
            ? error.message
            : "An error occurred while processing your submission",
          "error"
        );
      }
      return null;
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const journeyError = useMemo(() => {
    if (journeyNotFound) {
      return "Journey not found";
    }

    return journeyErrorObj?.message ?? null;
  }, [journeyErrorObj, journeyNotFound]);

  const isLoading = journeyLoading || enquiryLoading;

  const contextValue = useMemo<UserJourneyContextType>(() => ({
    journey: journey ?? null,
    journeyVersion: journeyVersion ?? null,
    journeyDisplayId,
    journeyLoading,
    journeyError,
    journeyNotFound,
    enquiry,
    enquiryLoading,
    enquiryError,
    isLoading,
    isSubmitting,
    updateStepData,
    moveToNextStep,
    createEnquiry,
    updateEnquiry,
    showNotification,
    turnstileTokenRef,
    turnstileFeatureEnabled,
  }), [
    journey,
    journeyVersion,
    journeyDisplayId,
    journeyLoading,
    journeyError,
    journeyNotFound,
    enquiry,
    enquiryLoading,
    enquiryError,
    isLoading,
    isSubmitting,
    updateStepData,
    moveToNextStep,
    createEnquiry,
    updateEnquiry,
    showNotification,
    turnstileFeatureEnabled,
  ]);

  return (
    <UserJourneyContext.Provider value={contextValue}>{children}</UserJourneyContext.Provider>
  );
};

export const useUserJourneyContext = () => {
  const context = useContext(UserJourneyContext);
  if (!context) {
    throw new Error("useUserJourneyContext must be used within a UserJourneyProvider");
  }
  return context;
};

export const useUserJourneyContextOptional = () => {
  return useContext(UserJourneyContext);
};
